import { useAuth } from "@/context/AuthContext";
import { Clock, Tag } from "lucide-react";

// Trial / launch-pricing status pills shown on the Vendor / Event Creator /
// Marketplace portal headers for invited users ONLY. Hidden entirely for any
// user that didn't sign up through the admin invite flow (no `invite_token`
// on their doc) or that doesn't have a trial window stamped yet.
//
// Timeline:
//   trial_end_date .................... first R149 charge
//   trial_end_date + 365d ............. R149 → R299 switchover
//
// Pills render as:
//   "90-day trial — ends {date}"         (shown while now < trial_end_date)
//   "Launch price until {date}"          (shown while trial_end_date <= now < trial_end_date + 365d)
//   After the launch window closes we hide both pills — the user is on the
//   normal R299 plan at that point and there's nothing invite-specific left
//   to signal.
export default function InviteTrialBadge() {
  const { user } = useAuth();
  if (!user?.invite_token || !user?.invite_trial_end_date) return null;

  const trialEnd = new Date(user.invite_trial_end_date);
  if (Number.isNaN(trialEnd.getTime())) return null;
  const launchEnd = new Date(trialEnd.getTime() + 365 * 86400000);
  const now = Date.now();

  const fmt = (d) => d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const daysLeft = (d) => Math.max(0, Math.ceil((d.getTime() - now) / 86400000));

  const inTrial = now < trialEnd.getTime();
  const inLaunch = !inTrial && now < launchEnd.getTime();
  if (!inTrial && !inLaunch) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="invite-trial-badge">
      {inTrial && (
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide bg-sage-light text-sage-dark border border-sage/40"
          data-testid="invite-trial-badge-trial"
          title={`${daysLeft(trialEnd)} days left in your free trial`}
        >
          <Clock className="w-3.5 h-3.5" />
          90-day trial — ends {fmt(trialEnd)}
        </span>
      )}
      {inLaunch && (
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide bg-terracotta-light text-terracotta-dark border border-terracotta/40"
          data-testid="invite-trial-badge-launch"
          title={`${daysLeft(launchEnd)} days of R149 launch pricing left`}
        >
          <Tag className="w-3.5 h-3.5" />
          Launch price until {fmt(launchEnd)}
        </span>
      )}
    </div>
  );
}
